import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import * as moment from "moment";
import { FiArrowLeft, FiCalendar, FiInbox } from "react-icons/fi";
import Card from "./card";
import MoneyballDatePicker from "../../common/components/datePicker";
import oldHistoryService from "../../../api/services/oldHistoryService";
import { setOldHistory } from "../../common/components/sidebar/sidebar.actions";
import {
  playSong,
  setOldHistoryDate,
  setOldHistoryDateOption,
} from "../../common/actions/common.actions";

const OldHistories = ({ setIsLoading }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { oldHistoryDate, oldHistoryDateOption } = useSelector(
    (state) => state.common
  );
  const [selectedDate, setSelectedDate] = useState(
    oldHistoryDate ? new Date(oldHistoryDate) : new Date()
  );
  const [oldHistories, setOldHistories] = useState([]);
  const [hasError, setHasError] = useState(false);

  const handleSongClick = () => dispatch(playSong());

  const handleBackClick = () => {
    dispatch(setOldHistoryDate(new Date().toISOString()));
    handleSongClick();
    navigate("/dashboard");
  };

  useEffect(() => {
    const resetDate = () => {
      dispatch(setOldHistoryDate(new Date().toISOString()));
    };
    window.addEventListener("popstate", resetDate);
    return () => window.removeEventListener("popstate", resetDate);
  }, [dispatch]);

  useEffect(() => {
    let date = selectedDate;
    if (oldHistoryDateOption === "today") {
      date = new Date();
    } else if (oldHistoryDateOption === "yesterday") {
      date = moment().subtract(1, "days").toDate();
    } else if (oldHistoryDateOption === "choose" && oldHistoryDate) {
      date = new Date(oldHistoryDate);
    }

    const formattedDate = moment(date).format("YYYY-MM-DD");
    setIsLoading(true);
    setHasError(false);

    oldHistoryService
      .bettingOldHistory({ startDate: formattedDate, endDate: formattedDate })
      .then((response) => response.json())
      .then((data) => {
        const histories = Array.isArray(data) ? data : [];
        setOldHistories(histories);
      })
      .catch((error) => {
        setOldHistories([]);
        setHasError(true);
        console.error("API request error:", error);
      })
      .finally(() => setIsLoading(false));
  }, [oldHistoryDate, oldHistoryDateOption, selectedDate, setIsLoading]);

  const selectDateOption = (option) => {
    handleSongClick();
    dispatch(setOldHistoryDateOption(option));
  };

  const handleDateChange = (date) => {
    if (!date) return;
    dispatch(setOldHistoryDate(date.toISOString()));
    dispatch(setOldHistoryDateOption("choose"));
    setSelectedDate(date);
  };

  const activeDate =
    oldHistoryDateOption === "today"
      ? new Date()
      : oldHistoryDateOption === "yesterday"
      ? moment().subtract(1, "days").toDate()
      : selectedDate;

  return (
    <div className="relative min-h-screen w-full min-w-0 flex-1 self-stretch overflow-hidden bg-gradient_start font-myanmarSansPro text-white">
      <div className="pointer-events-none absolute -right-24 top-20 h-72 w-72 rounded-full bg-emerald-300/10 blur-3xl" />

      <header className="relative z-30 bg-header_green shadow-lg shadow-black/20">
        <div className="mx-auto flex min-h-[60px] w-full max-w-2xl items-center px-4 py-2">
          <button
            type="button"
            onClick={handleBackClick}
            aria-label="Back to dashboard"
            className="flex h-10 w-10 flex-none items-center justify-center rounded-lg border border-white/10 bg-white/10 transition hover:bg-white/20 active:scale-95"
          >
            <FiArrowLeft className="h-5 w-5" />
          </button>
          <h1 className="ml-3 text-base font-semibold leading-[1.8]">
            {t("oldHistories")}
          </h1>
          <div className="ml-auto flex items-center gap-2 text-xs text-white/55">
            <FiCalendar className="h-4 w-4" />
            <span className="font-sans">{moment(activeDate).format("YYYY-MM-DD")}</span>
          </div>
        </div>
      </header>

      <main className="relative z-10 mx-auto flex h-[calc(100vh-60px)] w-full max-w-2xl flex-col px-4 pb-5 pt-4">
        <section className="flex-none rounded-lg border border-white/10 bg-header_green/65 p-1.5 shadow-xl shadow-black/15 backdrop-blur-md">
          <div className="grid grid-cols-3 gap-1.5">
            <FilterButton
              active={oldHistoryDateOption === "today"}
              onClick={() => selectDateOption("today")}
            >
              {t("today")}
            </FilterButton>
            <FilterButton
              active={oldHistoryDateOption === "yesterday"}
              onClick={() => selectDateOption("yesterday")}
            >
              {t("yesterday")}
            </FilterButton>
            <MoneyballDatePicker
              selected={selectedDate}
              onChange={handleDateChange}
              maxDate={new Date()}
              variant="filter"
              active={oldHistoryDateOption === "choose"}
              ariaLabel="Choose betting history date"
            />
          </div>
        </section>

        <div className="mt-4 flex-1 overflow-y-auto overscroll-contain pb-2">
          {oldHistories.length > 0 ? (
            <div className="space-y-3">
              {oldHistories.map((data, index) => (
                <Link
                  key={data?.gamblingId || index}
                  className="block"
                  to={`/old-matches/${data?.gamblingId}`}
                  onClick={() => {
                    dispatch(setOldHistory(data));
                    handleSongClick();
                  }}
                >
                  <Card data={data} />
                </Link>
              ))}
            </div>
          ) : (
            <div className="flex h-full min-h-[280px] flex-col items-center justify-center px-6 text-center">
              <div className="flex h-12 w-12 items-center justify-center rounded-lg border border-white/10 bg-header_green/50 text-white/45">
                <FiInbox className="h-6 w-6" />
              </div>
              <p className="mt-4 max-w-xs text-sm leading-[1.8] text-white/65">
                {hasError ? t("sendingFail") : t("noData")}
              </p>
              <p className="mt-1 font-sans text-xs text-white/35">
                {moment(activeDate).format("YYYY-MM-DD")}
              </p>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

const FilterButton = ({ active, children, ...props }) => (
  <button
    type="button"
    className={`min-h-[48px] rounded-lg px-2 py-1 text-xs font-medium leading-[1.8] transition ${
      active
        ? "bg-white text-header_green shadow-md"
        : "text-white/60 hover:bg-white/10 hover:text-white"
    }`}
    {...props}
  >
    {children}
  </button>
);

export default OldHistories;
